// =============================================================
// ChatPage — WebSocket-backed chat with markdown rendering,
// thinking blocks, and a typing indicator.
// =============================================================

import { useEffect, useRef, useState } from 'react'
import { authFetch } from '../lib/auth'
import { renderMarkdown } from '../lib/markdown'
import { useChatSocket } from '../lib/sockets'
import type { ChatMessage } from '../lib/types'

interface ChatPageProps {
  active: boolean
  onUserActivity: () => void
}

const MAX_TEXTAREA_HEIGHT = 120

function formatTs(ts?: string): string {
  if (!ts) return ''
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

export function ChatPage({ active, onUserActivity }: ChatPageProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [ccLive, setCcLive] = useState(false)
  const [typingLabel, setTypingLabel] = useState('')
  const [input, setInput] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const messagesRef = useRef<HTMLDivElement>(null)

  const { markSeen } = useChatSocket({
    onHistory: (msgs) => setMessages(msgs),
    onMessage: (m) => {
      setMessages((prev) => [...prev, m])
      onUserActivity()
    },
    onTyping: (state, tool) => {
      if (state === 'thinking') setTypingLabel('💭 想...')
      else if (state === 'tool') setTypingLabel(`🔧 ${tool || 'tool'}`)
      else setTypingLabel('')
    },
    onCcStatus: (alive) => setCcLive(alive),
  })

  // Scroll to bottom on new messages
  useEffect(() => {
    const el = messagesRef.current
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight
    })
  }, [messages, typingLabel])

  // Also scroll when the mobile keyboard resizes the viewport
  useEffect(() => {
    if (!('visualViewport' in window)) return
    const vv = window.visualViewport!
    const handler = () => {
      const el = messagesRef.current
      if (!el) return
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight
      })
    }
    vv.addEventListener('resize', handler)
    return () => vv.removeEventListener('resize', handler)
  }, [])

  function autoResize(el: HTMLTextAreaElement) {
    el.style.height = 'auto'
    const newH = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)
    el.style.height = newH + 'px'
    el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden'
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value)
    autoResize(e.target)
  }

  function handleKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && e.metaKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  async function handleSend() {
    const text = input.trim()
    if (!text) return
    onUserActivity()
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text, ts: new Date().toISOString() },
    ])
    setInput('')
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.style.height = 'auto'
    }
    try {
      const resp = await authFetch('/api/chat-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: text }),
      })
      const data = (await resp.json()) as { id?: string; ok?: boolean }
      if (data.id) markSeen(data.id)
      if (!data.ok) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: '⚠️ 发送失败，请重试', ts: new Date().toISOString() },
        ])
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '⚠️ 网络错误，请重试', ts: new Date().toISOString() },
      ])
    }
  }

  return (
    <div className={`page ${active ? 'active' : ''}`}>
      <div className="page-inner chat-page">
        {/* Status bar */}
        <div className="chat-status-bar">
          <div className={`chat-dot ${ccLive ? 'live' : ''}`} />
          <div className="chat-name">琥珀</div>
          <span className="chat-activity">{typingLabel}</span>
        </div>

        {/* Messages */}
        <div className="chat-messages" id="chatMessages" ref={messagesRef}>
          {messages.length === 0 && (
            <div className="chat-empty" id="chatEmpty">
              <span>💬</span>
              <div>开始聊天吧</div>
            </div>
          )}

          {messages.map((m, i) => (
            <ChatMessageView key={(m.id || '') + '-' + i} msg={m} />
          ))}

          {typingLabel && (
            <div className={`chat-typing active`}>{typingLabel}</div>
          )}
        </div>

        {/* Input */}
        <div className="chat-input-bar">
          <textarea
            ref={inputRef}
            className="chat-input"
            rows={1}
            placeholder="🐱🧡…"
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKey}
          />
          <button
            type="button"
            className="chat-send"
            onClick={handleSend}
            disabled={!input.trim()}
          >
            ↑
          </button>
        </div>
      </div>
    </div>
  )
}

function ChatMessageView({ msg }: { msg: ChatMessage }) {
  const ts = formatTs(msg.ts)
  return (
    <>
      {msg.role === 'assistant' && msg.thinking && (
        <div className="chat-msg thinking">
          <details className="msg-thinking">
            <summary>思考中…</summary>
            <div className="msg-thinking-content">{msg.thinking}</div>
          </details>
        </div>
      )}
      <div className={`chat-msg ${msg.role}`}>
        {msg.role === 'assistant' ? (
          <div
            // Markdown rendered and sanitized server-side-safe via marked+DOMPurify
            dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
          />
        ) : (
          <div>{msg.content}</div>
        )}
        {ts && <span className="ts">{ts}</span>}
      </div>
    </>
  )
}